import React from 'react'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'

import { ArrayLevelError, RemoveRowButton, RowError } from '../pathMapping/SourceRow'

import { DIRECTION_OPTIONS, GENERIC_FIELDS } from './genericFields'

const FIELD = GENERIC_FIELDS.directionMap

/**
 * Maps the vendor's own direction codes onto inbound/outbound.
 *
 * Stored as a list of { from, to } rather than an object, and the backend takes it
 * that way for two reasons a dict cannot satisfy: a JSON object with integer-like
 * keys ({"1": "inbound"}) is reordered to ascending on every render, so rows jump
 * while the admin is typing, and a duplicated code would silently collapse instead of
 * erroring.
 *
 * Only needed for codes the shared mapping cannot know. incoming/outgoing/in/out
 * already resolve without a map.
 */
export const GenericDirectionMapFields = () => {
  const { values, errors, setFieldValue } = useFormikContext()

  // Stored indices are preserved so errors[FIELD][storedIndex] keeps pointing at the
  // row that caused it, exactly as in the shared row containers.
  const rows = (Array.isArray(values[FIELD]) ? values[FIELD] : [])
    .map((row, storedIndex) => ({ row, storedIndex }))
    .filter(({ row }) => row && typeof row === 'object')
  const storedRows = rows.map(({ row }) => row)

  const setRow = (index, patch) =>
    setFieldValue(
      FIELD,
      storedRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    )
  const addRow = () => setFieldValue(FIELD, [...storedRows, { from: '', to: '' }])
  const removeRow = (index) =>
    setFieldValue(
      FIELD,
      storedRows.filter((_, rowIndex) => rowIndex !== index)
    )

  const rowError = (storedIndex, name) => errors[FIELD]?.[storedIndex]?.[name]

  return (
    <FormField data-testid="generic-direction-map">
      <label>Direction Value Mapping</label>
      <div className="form-caption">
        Only needed if the vendor sends its own codes. Values like <code>inbound</code>,{' '}
        <code>outbound</code>, <code>incoming</code>, <code>outgoing</code>, <code>in</code> and{' '}
        <code>out</code> are understood already. Matching ignores case. A value with no row leaves
        the direction unset.
      </div>
      {!isEmpty(rows) && (
        <FormFieldGrid>
          <FormField span={6}>
            <label>Vendor sends</label>
          </FormField>
          <FormField span={6}>
            <label>Balto direction</label>
          </FormField>
        </FormFieldGrid>
      )}
      {rows.map(({ row, storedIndex }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <FormFieldGrid key={index}>
          <FormField span={6}>
            <TextInput
              value={row.from || ''}
              placeholder="1"
              data-testid={`generic-direction-from-${index}`}
              aria-label={`Vendor value ${index + 1}`}
              onChange={(event) => setRow(index, { from: event.target.value })}
            />
            <RowError message={rowError(storedIndex, 'from')} />
          </FormField>
          <FormField span={6}>
            <Select
              options={DIRECTION_OPTIONS}
              value={row.to}
              isClearable={false}
              placeholder="Direction…"
              inputId={`generic-direction-to-${index}`}
              aria-label={`Balto direction ${index + 1}`}
              classNamePrefix="generic-direction-to"
              onChange={(option) => setRow(index, { to: option ? option.value : '' })}
            />
            <RowError message={rowError(storedIndex, 'to')} />
          </FormField>
          <RemoveRowButton
            label="Remove direction mapping"
            testId={`generic-direction-remove-${index}`}
            onClick={() => removeRow(index)}
          />
        </FormFieldGrid>
      ))}
      <Button type="button" onClick={addRow} data-testid="generic-direction-add">
        Add mapping
      </Button>
      <ArrayLevelError error={errors[FIELD]} />
    </FormField>
  )
}
